import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { LanguageProvider } from "./i18n/LanguageContext";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import ProfileEdit from "./pages/ProfileEdit";
import History from "./pages/History";
import Gallery from "./pages/Gallery";
import SMSTerms from "./pages/SMSTerms";
import Clients from "./pages/Clients";
import Vitals from "./pages/Vitals";
import Documents from "./pages/Documents";
import Calendar from "./pages/Calendar";
import Resources from "./pages/Resources";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminDashboard from "./pages/AdminDashboard";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Messages from "./pages/Messages";
import HelpCenter from "./pages/HelpCenter";
import EmergencyAccess from "./pages/EmergencyAccess";
import CaregiverDirectory from "./pages/CaregiverDirectory";
import News from "./pages/News";
import CareLogsList from "./pages/CareLogsList";
import CareLogWizard from "./pages/CareLogWizard";
import CareLogView from "./pages/CareLogView";
import CarePlanBuilder from "./pages/CarePlanBuilder";
import Plans from "./pages/Plans";
import PaymentSuccess from "./pages/PaymentSuccess";
import FamilyDashboard from "./pages/FamilyDashboard";
import FamilyInviteAccept from "./pages/FamilyInviteAccept";
import WelcomeModal from "./components/WelcomeModal";
import WhatsNewBanner from "./components/WhatsNewBanner";
import { requestNotificationPermission, isNotificationsSupported, notifyCheckinDue, notifyCheckinOverdue } from "./utils/notifications";
import InstallPrompt from "./components/InstallPrompt";
import CompanyFooter from "./components/CompanyFooter";
import SplashScreen from "./components/SplashScreen";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Request push notification permission
    if (isNotificationsSupported()) {
      requestNotificationPermission();
    }
  };

  // Check-in notification monitor
  useEffect(() => {
    if (!user) return;
    const checkInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/checkin/status`);
        const { overdue, hours_until_due } = res.data;
        if (overdue) {
          notifyCheckinOverdue(user.name);
        } else if (hours_until_due <= 1) {
          notifyCheckinDue(user.name);
        }
      } catch {}
    }, 5 * 60 * 1000); // Check every 5 minutes
    return () => clearInterval(checkInterval);
  }, [user]);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Handle 404.html redirect for SPA routing in production
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    if (redirect) {
      window.history.replaceState(null, '', redirect);
      navigate(redirect, { replace: true });
    }
  }, [navigate]);

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <SplashScreen />
      <InstallPrompt />
      {user && <WelcomeModal user={user} setUser={setUser} />}
      {user && <WhatsNewBanner user={user} />}
      <Routes>
        <Route path="/" element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} /> : <Landing user={null} />} />
        <Route 
          path="/auth" 
          element={user ? <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} /> : <Auth onLogin={login} />} 
        />
        <Route path="/sms-terms" element={<SMSTerms />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/help" element={<HelpCenter user={user} onLogout={logout} />} />
        <Route path="/emergency-access" element={<EmergencyAccess />} />
        <Route 
          path="/messages" 
          element={user ? <Messages user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/admin" 
          element={user?.role === 'admin' ? <AdminDashboard user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/dashboard" 
          element={user ? <Dashboard user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/profile" 
          element={user ? <Profile user={user} setUser={setUser} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/profile/edit" 
          element={user ? <ProfileEdit user={user} setUser={setUser} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/history" 
          element={user ? <History user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/gallery" 
          element={user ? <Gallery user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/clients" 
          element={user ? <Clients user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/vitals" 
          element={user ? <Vitals user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/documents" 
          element={user ? <Documents user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/calendar" 
          element={user ? <Calendar user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/resources" 
          element={<Resources user={user} onLogout={logout} />} 
        />
        <Route 
          path="/directory" 
          element={user ? <CaregiverDirectory user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/news" 
          element={user ? <News user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/clients/:clientId/care-logs" 
          element={user ? <CareLogsList user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/clients/:clientId/care-logs/new" 
          element={user ? <CareLogWizard user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/clients/:clientId/care-logs/:logId/edit" 
          element={user ? <CareLogWizard user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/clients/:clientId/care-logs/:logId/view" 
          element={user ? <CareLogView user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/clients/:clientId/care-plan" 
          element={user ? <CarePlanBuilder user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/plans" 
          element={user ? <Plans user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/payment/success" 
          element={user ? <PaymentSuccess /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/family/dashboard" 
          element={user ? <FamilyDashboard user={user} onLogout={logout} /> : <Navigate to="/auth" />} 
        />
        <Route 
          path="/family/invite/:token" 
          element={<FamilyInviteAccept user={user} onLogout={logout} />} 
        />
        <Route 
          path="/forgot-password" 
          element={<ForgotPassword />} 
        />
        <Route 
          path="/reset-password" 
          element={<ResetPassword />} 
        />
      </Routes>
      <CompanyFooter />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
export { API };